import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CheckCircle2, ChevronLeft, ChevronRight, Car, CreditCard, User, Home, Briefcase, Building2, AlertCircle, FileText } from 'lucide-react';
import { useForm } from '../context/FormContext';
import { useSource } from '../context/SourceContext';
import { validateStep as runStepValidation } from '../lib/stepValidation';
import { Button } from './ui/button';
import { DealerNameStep } from './steps/DealerNameStep';
import { VehicleStep } from './steps/VehicleStep';
import { FinanceTypeStep } from './steps/FinanceTypeStep';
import { ApplicationTypeStep } from './steps/ApplicationTypeStep';
import { DrivingLicenceStep } from './steps/DrivingLicenceStep';
import { MaritalStatusStep } from './steps/MaritalStatusStep';
import { PassportStep } from './steps/PassportStep';
import { NationalityStep } from './steps/NationalityStep';
import { DateOfBirthStep } from './steps/DateOfBirthStep';
import { EmploymentStatusStep } from './steps/EmploymentStatusStep';
import { EmploymentDetailsStep } from './steps/EmploymentDetailsStep';
import { IncomeStep } from './steps/IncomeStep';
import { AddressStep } from './steps/AddressStep';
import { BankDetailsStep } from './steps/BankDetailsStep';
import { PersonalAndContactStep } from './steps/PersonalAndContactStep';
import { NotesStep } from './steps/NotesStep';
import { ReviewStep } from './steps/ReviewStep';
import { TrustBadge } from './TrustBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Step order: Dealer > Vehicle > Finance Type > Application Type > Licence > Marital > Passport > Nationality > DOB > Personal+Contact > Address > Employment > Income > Bank > Notes > Review
const STEPS = [
  { id: 'dealer', label: 'Dealer', icon: Building2, component: DealerNameStep },
  { id: 'vehicle', label: 'Vehicle', icon: Car, component: VehicleStep },
  { id: 'finance-type', label: 'Finance', icon: CreditCard, component: FinanceTypeStep },
  { id: 'application-type', label: 'App Type', icon: Building2, component: ApplicationTypeStep },
  { id: 'licence', label: 'Licence', icon: Car, component: DrivingLicenceStep },
  { id: 'marital', label: 'Marital', icon: User, component: MaritalStatusStep },
  { id: 'passport', label: 'Passport', icon: User, component: PassportStep },
  { id: 'nationality', label: 'Nationality', icon: User, component: NationalityStep },
  { id: 'dob', label: 'DOB', icon: User, component: DateOfBirthStep },
  { id: 'personal-contact', label: 'Your Details', icon: User, component: PersonalAndContactStep },
  { id: 'address', label: 'Address', icon: Home, component: AddressStep },
  { id: 'employment-status', label: 'Employment', icon: Briefcase, component: EmploymentStatusStep },
  { id: 'employment-details', label: 'Job Details', icon: Briefcase, component: EmploymentDetailsStep },
  { id: 'income', label: 'Income', icon: CreditCard, component: IncomeStep },
  { id: 'bank', label: 'Bank', icon: CreditCard, component: BankDetailsStep },
  { id: 'notes', label: 'Notes', icon: FileText, component: NotesStep },
  { id: 'review', label: 'Review', icon: CheckCircle2, component: ReviewStep },
];

const STEP_GROUPS = [
  { label: 'Dealer', steps: [0] },
  { label: 'Vehicle', steps: [1, 2, 3] },
  { label: 'You', steps: [4, 5, 6, 7, 8, 9] },
  { label: 'Address', steps: [10] },
  { label: 'Work', steps: [11, 12, 13] },
  { label: 'Bank', steps: [14] },
  { label: 'Notes', steps: [15] },
  { label: 'Review', steps: [16] },
];

export const FormWizard = ({ onBack }) => {
  const { source } = useSource();
  const { currentStep, nextStep, prevStep, formData, updateFormData, editingFromReview, setEditingFromReview, goToStep, extractedData, pendingResume, acceptResume, dismissResume, applicationMode } = useForm();
  const [validationError, setValidationError] = useState('');
  const formContentRef = useRef(null);
  const touchStartRef = useRef(null);
  
  // Anonymous session ID for funnel tracking (no PII)
  const sessionIdRef = useRef(null);
  if (!sessionIdRef.current) {
    sessionIdRef.current = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  // Track step views for funnel analysis
  useEffect(() => {
    const step = STEPS[currentStep];
    if (!step) return;
    fetch(`${API_URL}/api/track/step-view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ step_id: step.id, step_index: currentStep, session_id: sessionIdRef.current })
    }).catch(() => {});
  }, [currentStep]);

  // Auto-scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);
  
  const currentStepData = STEPS[currentStep];
  const presetDealer = source?.prefill_dealer_name ? (source.dealer_name_prefill || source.name || '').trim() : '';
  const firstStep = presetDealer ? 1 : 0;
  const visibleGroups = presetDealer ? STEP_GROUPS.slice(1) : STEP_GROUPS;

  // Dealer link: fill the dealer name silently and start at the Vehicle step.
  useEffect(() => {
    if (!presetDealer) return;
    if (formData.dealer_name !== presetDealer) updateFormData({ dealer_name: presetDealer });
    if (currentStep === 0) goToStep(1);
  }, [presetDealer, formData.dealer_name, currentStep]);
  
  // Get review step index (last step)
  const reviewStepIndex = STEPS.length - 1;
  
  // Handle skip to review from dealer step
  const handleSkipToReview = () => {
    setValidationError('');
    goToStep(reviewStepIndex);
  };
  
  // Render the current step component with any required props
  const autoAdvanceSteps = ['licence', 'passport', 'marital', 'employment-status'];

  const renderCurrentStep = () => {
    const StepComponent = currentStepData?.component;
    if (!StepComponent) return null;
    
    // DealerNameStep gets both `onSkipToReview` and `onAutoAdvance` —
    // the latter powers an inline Next button placed right under the input
    // so users don't have to hunt for the global Next button hidden below
    // the on-screen keyboard on mobile.
    if (currentStepData.id === 'dealer') {
      return <StepComponent onSkipToReview={handleSkipToReview} onAutoAdvance={() => nextStep()} />;
    }

    // Auto-advance steps — skip to next after selection
    if (autoAdvanceSteps.includes(currentStepData.id)) {
      return <StepComponent onAutoAdvance={() => nextStep()} />;
    }
    
    return <StepComponent />;
  };
  
  const handleBackToReview = () => {
    setEditingFromReview(false);
    setValidationError('');
    goToStep(reviewStepIndex);
  };
  
  // Find which group the current step belongs to
  const getCurrentGroup = () => {
    for (let i = 0; i < visibleGroups.length; i++) {
      if (visibleGroups[i].steps.includes(currentStep)) {
        return i;
      }
    }
    return 0;
  };
  
  const currentGroup = getCurrentGroup();

  // Validation rules for each step are defined in /app/frontend/src/lib/stepValidation.js
  // so the same rules can be reused by ReviewStep's pre-submit gating.
  const validateStep = (stepId) => runStepValidation(stepId, formData, applicationMode, { allowNoReg: source?.submission_mode === 'pdf_email' });

  const handleNext = () => {
    const stepId = STEPS[currentStep]?.id;
    const error = validateStep(stepId);
    
    if (error) {
      setValidationError(error);
      return;
    }
    
    setValidationError('');
    if (currentStep < STEPS.length - 1) {
      nextStep();
    }
  };

  const handleBack = () => {
    setValidationError('');
    if (currentStep > firstStep) {
      prevStep();
    } else {
      onBack();
    }
  };

  // Swipe gesture handlers for mobile navigation
  const handleTouchStart = useCallback((e) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (!touchStartRef.current) return;
    const deltaX = e.changedTouches[0].clientX - touchStartRef.current.x;
    const deltaY = e.changedTouches[0].clientY - touchStartRef.current.y;
    // Only trigger if horizontal swipe is dominant and > 80px
    if (Math.abs(deltaX) > 80 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
      if (deltaX < 0) {
        // Swipe left → next
        handleNext();
      } else {
        // Swipe right → back
        handleBack();
      }
    }
    touchStartRef.current = null;
  }, [currentStep, formData, editingFromReview, extractedData]);

  // Pre-compute current step validation so the Next button is disabled
  // (rather than just shouting after the click) whenever a required field
  // is missing — covers Address street/town/residential_status etc.
  const currentStepError = currentStep < STEPS.length - 1
    ? validateStep(STEPS[currentStep]?.id)
    : null;

  return (
    <>
    {pendingResume && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
        data-testid="resume-prompt-overlay"
      >
        <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">Resume your previous application?</h2>
          <p className="text-sm text-slate-600">
            We found a partially completed application saved on this device.
            Pick up where you left off, or start fresh.
          </p>
          <p className="text-xs text-slate-400">
            Saved progress stays on your device only — nothing was sent to our servers.
            It auto-expires after 24 hours.
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={dismissResume}
              className="flex-1"
              data-testid="resume-prompt-discard"
            >
              Start fresh
            </Button>
            <Button
              onClick={acceptResume}
              className="flex-1 brand-bg text-white"
              data-testid="resume-prompt-accept"
            >
              Resume
            </Button>
          </div>
        </div>
      </div>
    )}
    <div className="animate-fade-in-up md:block" data-testid="form-wizard"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Title */}
      <div className="bg-white py-3 md:py-4 border-b border-slate-100">
        <h1 className="text-lg md:text-xl font-semibold text-slate-700 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Vehicle Finance Application
        </h1>
        <TrustBadge />
      </div>
      
      {/* Progress Bar */}
      <div className="bg-slate-50 border-b border-slate-200 px-2 py-2 md:py-4">
        <div className="max-w-3xl mx-auto">
          {/* Step Groups - HIDDEN on mobile, visible on md+ */}
          <div className="hidden md:flex items-center justify-between mb-3 overflow-x-auto scrollbar-hide">
            {visibleGroups.map((group, index) => (
              <div key={group.label} className="flex items-center flex-shrink-0">
                <button
                  onClick={() => {
                    if (index <= currentGroup) {
                      setValidationError('');
                      goToStep(group.steps[0]);
                    }
                  }}
                  disabled={index > currentGroup}
                  className={`flex flex-col items-center ${index <= currentGroup ? 'cursor-pointer' : 'cursor-default opacity-60'}`}
                  data-testid={`step-group-${index}`}
                >
                  <div 
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                      index < currentGroup 
                        ? 'brand-bg text-white' 
                        : index === currentGroup 
                          ? 'brand-bg text-white ring-4 brand-ring' 
                          : 'bg-slate-200 text-slate-500'
                    }`}
                  >
                    {index + 1}
                  </div>
                  <span className={`text-xs mt-1 whitespace-nowrap ${
                    index === currentGroup ? 'brand-text font-medium' : 'text-slate-500'
                  }`}>
                    {group.label}
                  </span>
                </button>
                {index < visibleGroups.length - 1 && (
                  <div className={`w-6 sm:w-10 h-0.5 mx-1 flex-shrink-0 ${
                    index < currentGroup ? 'brand-bg' : 'bg-slate-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
          
          {/* Current Step Progress */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">
              Step {currentStep + 1 - firstStep} of {STEPS.length - firstStep}
            </span>
            <span className="font-medium brand-text">
              {currentStepData?.label}
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full brand-bg transition-all duration-300 ease-out"
              style={{ width: `${((currentStep + 1 - firstStep) / (STEPS.length - firstStep)) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Step Content — grows to fill space on mobile, pushing nav to bottom */}
      <div className="flex-1 p-4 md:p-8 pb-24 md:pb-8" ref={formContentRef}>
        {currentStep === STEPS.length - 1 ? (
          <ReviewStep />
        ) : (
          renderCurrentStep()
        )}
      </div>
      </div>

      {/* Navigation — fixed at bottom on mobile, OUTSIDE animated parent so fixed positioning works */}
      <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-slate-200 p-3 bg-white/95 backdrop-blur-sm md:static md:p-6 md:bg-slate-50 md:backdrop-blur-none">
        <div className="max-w-3xl mx-auto">
          {(validationError || currentStepError) && (
            <div className={`flex items-center gap-2 mb-3 p-2.5 md:p-3 rounded-lg text-sm ${validationError ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-amber-50 border border-amber-200 text-amber-800'}`}>
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {validationError || currentStepError}
            </div>
          )}
          <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={handleBack}
            className="flex items-center gap-2 h-11 md:h-10 px-4 md:px-4 text-base md:text-sm"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </Button>
          
          <div className="flex gap-2 md:gap-3">
            {/* Skip to Review - shows when OCR data exists OR editing from review */}
            {(editingFromReview || extractedData) && currentStep < STEPS.length - 1 && (
              <Button
                variant="outline"
                onClick={handleBackToReview}
                className="flex items-center gap-1 md:gap-2 h-11 md:h-10 px-3 md:px-4 text-sm brand-border brand-text hover:opacity-80"
                data-testid="back-to-review-btn"
              >
                <span className="hidden sm:inline">Skip to</span> Review
              </Button>
            )}
            
            {currentStep < STEPS.length - 1 && (
              <Button
                onClick={handleNext}
                disabled={!!currentStepError}
                title={currentStepError || undefined}
                aria-disabled={!!currentStepError}
                className="flex items-center gap-2 h-11 md:h-10 px-5 md:px-4 text-base md:text-sm brand-bg hover:opacity-90 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="next-btn"
              >
                Next Step
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}
          </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FormWizard;
